// ── 전체 당첨 이력 페이지네이션 orchestration (Supabase/네트워크 의존 없음) ──
// useDrawHistory 의 페이지 수집 로직을 순수 함수로 분리.
// fetchCount / fetchPage 를 인자로 받아 누락 없이 전체를 수집하고, id 기준 중복 제거 +
// 최신 회차(내림차순) 정렬을 수행한다.

import { DRAW_PAGE_SIZE, type Draw } from '@/lib/drawCore';

export interface CountResult {
  count: number;
  error: boolean;
}

export interface PageResult {
  rows: Draw[];
  invalid: number;
  error: boolean;
}

export interface CollectAllDrawsResult {
  draws: Draw[];
  invalidCount: number;
  dbCount: number;
  error: boolean;
  aborted: boolean;
}

/**
 * fetchCount 로 전체 건수를 확인한 뒤 페이지 단위로 순차 수집한다.
 * - count 실패 → 페이지 요청 없이 error 반환
 * - 중간 페이지 실패 → 부분 데이터를 반환하지 않고 error 반환
 * - AbortSignal 중단 → 이후 페이지 요청 없이 aborted 반환
 * - id 기준 중복 제거 + 최신 회차(내림차순) 정렬
 * - 비정상 행은 invalidCount 로 집계(반환 결과에서 제외)
 */
export async function collectAllDraws(
  fetchCount: (signal: AbortSignal) => Promise<CountResult>,
  fetchPage: (from: number, to: number, signal: AbortSignal) => Promise<PageResult>,
  signal: AbortSignal,
  pageSize: number = DRAW_PAGE_SIZE,
): Promise<CollectAllDrawsResult> {
  // pageSize 는 1 이상의 정수만 허용한다. 잘못된 값은 즉시 구조화된 실패로 반환하고
  // fetchCount/fetchPage 를 한 번도 호출하지 않는다(fail-closed).
  if (!Number.isInteger(pageSize) || pageSize < 1) {
    return { draws: [], invalidCount: 0, dbCount: 0, error: true, aborted: false };
  }

  if (signal.aborted) {
    return { draws: [], invalidCount: 0, dbCount: 0, error: false, aborted: true };
  }

  const { count, error: countFailed } = await fetchCount(signal);
  if (signal.aborted) {
    return { draws: [], invalidCount: 0, dbCount: 0, error: false, aborted: true };
  }
  if (countFailed) {
    return { draws: [], invalidCount: 0, dbCount: 0, error: true, aborted: false };
  }

  const total = count;
  const collected: Draw[] = [];
  let invalid = 0;
  let from = 0;
  while (from < total) {
    if (signal.aborted) {
      return { draws: [], invalidCount: 0, dbCount: total, error: false, aborted: true };
    }
    const to = Math.min(from + pageSize - 1, total - 1);
    const { rows, invalid: pageInvalid, error: pageFailed } = await fetchPage(from, to, signal);
    if (signal.aborted) {
      return { draws: [], invalidCount: 0, dbCount: total, error: false, aborted: true };
    }
    if (pageFailed) {
      // 부분 데이터를 반환하지 않는다
      return { draws: [], invalidCount: 0, dbCount: total, error: true, aborted: false };
    }
    collected.push(...rows);
    invalid += pageInvalid;
    from = to + 1;
  }

  // id 기준 중복 제거
  const seen = new Set<number>();
  const deduped: Draw[] = [];
  for (const d of collected) {
    if (!seen.has(d.id)) {
      seen.add(d.id);
      deduped.push(d);
    }
  }
  // 최신 회차 우선(내림차순)
  deduped.sort((a, b) => b.id - a.id);

  return {
    draws: deduped,
    invalidCount: invalid,
    dbCount: total,
    error: false,
    aborted: false,
  };
}